import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiGet } from '../../utils/apiFetch'
import apiPath from '../../utils/apiPath'
const Badge = ({ children, color = 'gray' }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-800`}>{children}</span>
)

const Section = ({ title, children }) => (
  <div className='mt-6'>
    <h3 className='text-base font-semibold mb-2 text-gray-800'>{title}</h3>
    <div className='bg-white rounded-lg border p-4'>{children}</div>
  </div>
)

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0
  return value !== undefined && value !== null && String(value).trim() !== ''
}

const CareerView = () => {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState(null)

  useEffect(() => {
    const load = async () => {
      const res = await apiGet(`${apiPath.careerById}/${params.id}`)
      setData(res?.data?.results || null)
    }
    load()
  }, [params.id])

  if (!data) return <div className='p-3'>{t('Loading...')}</div>

  const salary = data.salaryRange || {}
  const benefits = data.benefits || []
  const skills = data.skills || []
  const benefitsAr = data.benefitsAr || []
  const skillsAr = data.skillsAr || []

  return (
    <div className='p-3 max-w-6xl mx-auto'>
      {/* Header */}
      <div className='bg-white rounded-lg border p-5'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-2xl font-bold text-gray-900'>{data.title}</h2>
            {hasValue(data.titleAr) ? (
              <p className='mt-1 text-lg text-gray-700' dir='rtl'>{data.titleAr}</p>
            ) : null}
            <div className='mt-2 flex flex-wrap gap-2'>
              <Badge color={data.status === 'active' ? 'green' : data.status === 'inactive' ? 'yellow' : 'gray'}>{data.status}</Badge>
              {data.isUrgent ? <Badge color='red'>{t('isUrgent') || 'Urgent'}</Badge> : null}
              {data.isRemote ? <Badge color='blue'>{t('isRemote') || 'Remote'}</Badge> : null}
            </div>
          </div>
          <div className='flex items-center gap-2'>
            <button className='px-3 py-2 rounded bg-[#8FC150] hover:bg-[#8FC150] text-white text-sm' onClick={() => navigate(`/careers/applications?careerId=${data._id}`)}>
              {t('View Applications')}
            </button>
            <div className='text-sm text-gray-600'>
              {t('O_CREATED_AT')}: {new Date(data.createdAt).toLocaleString()}
            </div>
          </div>
        </div>

        <div className='grid md:grid-cols-2 gap-4 mt-4 text-sm'>
          <div><strong>{t('CAREERS_DEPARTMENT')}:</strong> {data.department}</div>
          {hasValue(data.departmentAr) ? (
            <div dir='rtl'><strong>{t('CAREERS_DEPARTMENT')} (Ar):</strong> {data.departmentAr}</div>
          ) : null}
          <div><strong>{t('O_LOCATION')}:</strong> {data.location}</div>
          {hasValue(data.locationAr) ? (
            <div dir='rtl'><strong>{t('O_LOCATION')} (Ar):</strong> {data.locationAr}</div>
          ) : null}
          <div><strong>{t('CAREERS_EMPLOYMENT_TYPE')}:</strong> {data.employmentType}</div>
          {hasValue(data.employmentTypeAr) ? (
            <div dir='rtl'><strong>{t('CAREERS_EMPLOYMENT_TYPE')} (Ar):</strong> {data.employmentTypeAr}</div>
          ) : null}
          <div><strong>{t('Experience')}:</strong> {data.experienceLevel}</div>
          {hasValue(data.experienceLevelAr) ? (
            <div dir='rtl'><strong>{t('Experience')} (Ar):</strong> {data.experienceLevelAr}</div>
          ) : null}
          <div><strong>{t('Application Deadline')}:</strong> {new Date(data.applicationDeadline).toLocaleDateString()}</div>
        </div>

        {salary && (salary.min || salary.max) ? (
          <div className='mt-3 text-sm'>
            <strong>{t('CAREERS_COMPENSATION')}:</strong> {salary.currency || 'AED'} {salary.min ?? 0} - {salary.max ?? 0} {salary.isNegotiable ? `(${t('isNegotiable') || 'Negotiable'})` : ''}
          </div>
        ) : null}
      </div>

      {/* Description */}
      <Section title={t('Description')}>
        <p className='text-sm whitespace-pre-wrap'>{data.description}</p>
      </Section>
      {hasValue(data.descriptionAr) ? (
        <Section title={`${t('Description')} (Ar)`}>
          <p className='text-sm whitespace-pre-wrap' dir='rtl'>{data.descriptionAr}</p>
        </Section>
      ) : null}

      {/* Requirements */}
      <Section title={t('Requirements')}>
        <p className='text-sm whitespace-pre-wrap'>{data.requirements}</p>
      </Section>
      {hasValue(data.requirementsAr) ? (
        <Section title={`${t('Requirements')} (Ar)`}>
          <p className='text-sm whitespace-pre-wrap' dir='rtl'>{data.requirementsAr}</p>
        </Section>
      ) : null}

      {/* Responsibilities */}
      <Section title={t('Responsibilities')}>
        <p className='text-sm whitespace-pre-wrap'>{data.responsibilities}</p>
      </Section>
      {hasValue(data.responsibilitiesAr) ? (
        <Section title={`${t('Responsibilities')} (Ar)`}>
          <p className='text-sm whitespace-pre-wrap' dir='rtl'>{data.responsibilitiesAr}</p>
        </Section>
      ) : null}

      {/* Benefits & Skills */}
      {(benefits.length || skills.length || benefitsAr.length || skillsAr.length) ? (
        <div className='grid md:grid-cols-2 gap-4 mt-6'>
          {benefits.length ? (
            <div className='bg-white rounded-lg border p-4'>
              <h3 className='text-base font-semibold mb-2 text-gray-800'>{t('Benefits')}</h3>
              <ul className='list-disc list-inside text-sm'>
                {benefits.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
            </div>
          ) : null}
          {benefitsAr.length ? (
            <div className='bg-white rounded-lg border p-4'>
              <h3 className='text-base font-semibold mb-2 text-gray-800'>{t('Benefits')} (Ar)</h3>
              <ul className='list-disc list-inside text-sm' dir='rtl'>
                {benefitsAr.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
            </div>
          ) : null}
          {skills.length ? (
            <div className='bg-white rounded-lg border p-4'>
              <h3 className='text-base font-semibold mb-2 text-gray-800'>{t('Skills')}</h3>
              <ul className='list-disc list-inside text-sm'>
                {skills.map((s, i) => (<li key={i}>{s}</li>))}
              </ul>
            </div>
          ) : null}
          {skillsAr.length ? (
            <div className='bg-white rounded-lg border p-4'>
              <h3 className='text-base font-semibold mb-2 text-gray-800'>{t('Skills')} (Ar)</h3>
              <ul className='list-disc list-inside text-sm' dir='rtl'>
                {skillsAr.map((s, i) => (<li key={i}>{s}</li>))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Questions */}
      {Array.isArray(data.questions) && data.questions.length > 0 ? (
        <Section title={t('Questions') || 'Questions'}>
          <div className='space-y-3'>
            {data.questions.map((q, i) => (
              <div key={i} className='text-sm border-b last:border-b-0 pb-3 last:pb-0'>
                <div className='font-medium text-gray-900'>
                  {i + 1}. {q.question}
                </div>
                {q.questionAr ? (
                  <div className='text-gray-700 mt-1' dir='rtl'>{q.questionAr}</div>
                ) : null}
                <div className='text-gray-600 mt-1'>
                  {q.type === 'dropdown' ? (t('Dropdown options') || 'Dropdown') : (t('Text answer') || 'Text')}
                </div>
                {q.type === 'dropdown' && Array.isArray(q.options) && q.options.length > 0 ? (
                  <ul className='list-disc list-inside mt-1 text-gray-700'>
                    {q.options.map((opt, oi) => {
                      const text = typeof opt === 'string' ? opt : opt?.text
                      const textAr = typeof opt === 'string' ? '' : opt?.textAr
                      if (!text) return null
                      return (
                        <li key={oi}>
                          {text}
                          {textAr ? <span dir='rtl'> / {textAr}</span> : null}
                        </li>
                      )
                    })}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {/* Meta */}
      {(data.metaTitle || data.metaDescription || data.metaKeyword || data.metaTitleAr || data.metaDescriptionAr || data.metaKeywordAr) ? (
        <Section title='SEO'>
          <div className='grid md:grid-cols-2 gap-3 text-sm'>
            {hasValue(data.metaTitle) ? <div><strong>Meta Title:</strong> {data.metaTitle}</div> : null}
            {hasValue(data.metaTitleAr) ? <div dir='rtl'><strong>Meta Title (Ar):</strong> {data.metaTitleAr}</div> : null}
            {hasValue(data.metaDescription) ? <div className='md:col-span-2'><strong>Meta Description:</strong> <span className='whitespace-pre-wrap'>{data.metaDescription}</span></div> : null}
            {hasValue(data.metaDescriptionAr) ? <div className='md:col-span-2' dir='rtl'><strong>Meta Description (Ar):</strong> <span className='whitespace-pre-wrap'>{data.metaDescriptionAr}</span></div> : null}
            {hasValue(data.metaKeyword) ? <div className='md:col-span-2'><strong>Meta Keywords:</strong> {data.metaKeyword}</div> : null}
            {hasValue(data.metaKeywordAr) ? <div className='md:col-span-2' dir='rtl'><strong>Meta Keywords (Ar):</strong> {data.metaKeywordAr}</div> : null}
          </div>
        </Section>
      ) : null}
    </div>
  )
}

export default CareerView
